import { createStyles, makeStyles } from "@material-ui/core/styles";
import { TextFieldProps as MuiTextFieldProps } from "@material-ui/core/TextField";
import CalendarIcon from "@material-ui/icons/CalendarTodayRounded";
import { KeyboardDatePicker, KeyboardDatePickerProps } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import clsx from "clsx";
import React, { useCallback } from "react";
import { Color } from "../../theme";
import { HasTestId, PlainFunctionComponent } from "../../types";

export interface DatePickerProps extends Omit<KeyboardDatePickerProps, "emptyLabel" | "onChange" | "variant">, HasTestId {
    /**
     * @param newValue date object type of your linked date-io adapter (Moment, DayJS, etc.) or `undefined`
     */
    onChange: (newValue: NonNullable<MaterialUiPickersDate> | undefined) => void;
    /**
     * Placeholder which should contain the localized date format, e.g. `TT.MM.YYYY`
     */
    placeholder?: MuiTextFieldProps["placeholder"];
}

export const DatePicker: PlainFunctionComponent<DatePickerProps> = ({
    disabled,
    helperText,
    InputProps,
    inputVariant = "filled",
    onChange,
    placeholder,
    required,
    testId,
    value,
    ...props
}) => {
    const handleChange = useCallback(
        (date: MaterialUiPickersDate) => {
            onChange(date ?? undefined); // prevent `null`
        },
        [onChange],
    );

    const classes = useStyles();
    return (
        <KeyboardDatePicker
            autoOk={true}
            data-testid={testId}
            disabled={disabled}
            disableToolbar={true}
            keyboardIcon={<CalendarIcon />}
            helperText={helperText || <span />} // at least a tag prevents helper text area from shrinking...
            InputProps={{
                className: clsx(classes.calendarIcon, { [classes.requiredInput]: required && !disabled && inputVariant !== "standard" }),
                placeholder: placeholder,
                ...InputProps,
            }}
            inputVariant={inputVariant}
            onChange={handleChange}
            PopoverProps={{ anchorOrigin: { horizontal: "left", vertical: "bottom" } }}
            required={required}
            value={value ?? null} // requires `null` as empty date instead of `undefined` which we use normally
            variant="inline"
            {...props}
        />
    );
};

const useStyles = makeStyles(
    createStyles({
        calendarIcon: {
            // override `margin`s from global `MuiInputAdornment: { positionEnd: ... }}`
            "& > .MuiInputAdornment-positionEnd": {
                marginLeft: 16,
                marginRight: -6,
            },
            "& .MuiIconButton-root.Mui-disabled": {
                color: Color.grey4,
            },
        },
        requiredInput: {
            backgroundColor: Color.grey2,
        },
    }),
);
